// Resolve a USPS state code for dashboard map coverage points.

const STATE_ABBR = {
  AL: 'Alabama', AK: 'Alaska', AZ: 'Arizona', AR: 'Arkansas', CA: 'California', CO: 'Colorado',
  CT: 'Connecticut', DE: 'Delaware', FL: 'Florida', GA: 'Georgia', HI: 'Hawaii', ID: 'Idaho',
  IL: 'Illinois', IN: 'Indiana', IA: 'Iowa', KS: 'Kansas', KY: 'Kentucky', LA: 'Louisiana',
  ME: 'Maine', MD: 'Maryland', MA: 'Massachusetts', MI: 'Michigan', MN: 'Minnesota',
  MS: 'Mississippi', MO: 'Missouri', MT: 'Montana', NE: 'Nebraska', NV: 'Nevada',
  NH: 'New Hampshire', NJ: 'New Jersey', NM: 'New Mexico', NY: 'New York', NC: 'North Carolina',
  ND: 'North Dakota', OH: 'Ohio', OK: 'Oklahoma', OR: 'Oregon', PA: 'Pennsylvania',
  RI: 'Rhode Island', SC: 'South Carolina', SD: 'South Dakota', TN: 'Tennessee', TX: 'Texas',
  UT: 'Utah', VT: 'Vermont', VA: 'Virginia', WA: 'Washington', WV: 'West Virginia',
  WI: 'Wisconsin', WY: 'Wyoming', DC: 'District of Columbia',
};

const NAME_TO_ABBR = Object.fromEntries(
  Object.entries(STATE_ABBR).map(([abbr, name]) => [name.toLowerCase(), abbr])
);

export function coerceStateCode(maybe) {
  if (!maybe || typeof maybe !== 'string') return null;
  const raw = maybe.trim();
  if (!raw) return null;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return NAME_TO_ABBR[raw.toLowerCase()] || null;
}

/** Try to read a state from a store address string (e.g. "…, Los Angeles, CA 90001"). */
export function stateFromStoreLocation(location) {
  if (!location || typeof location !== 'string') return null;
  const trimmed = location.trim();
  const abbrTail = trimmed.match(/,\s*([A-Za-z]{2})(?:\s+\d{5}(?:-\d{4})?)?\s*$/);
  if (abbrTail) return coerceStateCode(abbrTail[1]);
  const nameTail = trimmed.match(/,\s*([A-Za-z][A-Za-z\s.]+?)(?:\s+\d{5}(?:-\d{4})?)?\s*$/);
  if (nameTail) return coerceStateCode(nameTail[1]);
  return null;
}

/**
 * Approximate US state from lat/lng (bounding boxes). Used when API state is missing.
 * Not survey-grade; sufficient for dashboard coverage counts.
 */
export function stateFromLatLng(lat, lng) {
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  // Continental US + HI/AK coarse boxes (lng, lat min/max)
  const boxes = [
    ['AL', -88.5, -84.9, 30.2, 35.0], ['AZ', -114.8, -109.0, 31.3, 37.0],
    ['AR', -94.6, -89.6, 33.0, 36.5], ['CA', -124.5, -114.1, 32.5, 42.1],
    ['CO', -109.1, -102.0, 37.0, 41.0], ['CT', -73.7, -71.8, 40.9, 42.1],
    ['DE', -75.8, -75.0, 38.4, 39.8], ['FL', -87.6, -80.0, 24.5, 31.0],
    ['GA', -85.6, -80.8, 30.4, 35.0], ['HI', -160.3, -154.8, 18.9, 22.3],
    ['ID', -117.2, -111.0, 42.0, 49.0], ['IL', -91.5, -87.5, 37.0, 42.5],
    ['IN', -88.1, -84.8, 37.8, 41.8], ['IA', -96.6, -90.1, 40.4, 43.5],
    ['KS', -102.1, -94.6, 37.0, 40.0], ['KY', -89.6, -82.0, 36.5, 39.2],
    ['LA', -94.0, -89.0, 29.0, 33.0], ['ME', -71.1, -66.9, 43.0, 47.5],
    ['MD', -79.5, -75.0, 37.9, 39.7], ['MA', -73.5, -69.9, 41.2, 42.9],
    ['MI', -90.4, -82.4, 41.7, 48.3], ['MN', -97.2, -89.5, 43.5, 49.4],
    ['MS', -91.7, -88.1, 30.2, 35.0], ['MO', -95.8, -89.1, 36.0, 40.6],
    ['MT', -116.1, -104.0, 44.4, 49.0], ['NE', -104.1, -95.3, 40.0, 43.0],
    ['NV', -120.0, -114.0, 35.0, 42.0], ['NH', -72.6, -70.6, 42.7, 45.3],
    ['NJ', -75.6, -73.9, 38.9, 41.4], ['NM', -109.1, -103.0, 31.3, 37.0],
    ['NY', -79.8, -71.9, 40.5, 45.0], ['NC', -84.3, -75.5, 33.8, 36.6],
    ['ND', -104.1, -96.6, 45.9, 49.0], ['OH', -84.8, -80.5, 38.4, 42.0],
    ['OK', -103.0, -94.4, 33.6, 37.0], ['OR', -124.6, -116.5, 42.0, 46.3],
    ['PA', -80.5, -74.7, 39.7, 42.3], ['RI', -71.9, -71.1, 41.1, 42.0],
    ['SC', -83.4, -78.5, 32.0, 35.2], ['SD', -104.1, -96.4, 42.5, 45.9],
    ['TN', -90.3, -81.6, 35.0, 36.7], ['TX', -106.6, -93.5, 25.8, 36.5],
    ['UT', -114.1, -109.0, 37.0, 42.0], ['VT', -73.4, -71.5, 42.7, 45.0],
    ['VA', -83.7, -75.2, 36.5, 39.5], ['WA', -124.8, -116.9, 45.5, 49.0],
    ['WV', -82.7, -77.7, 37.2, 40.6], ['WI', -92.9, -86.8, 42.5, 47.1],
    ['WY', -111.1, -104.0, 41.0, 45.0], ['AK', -170.0, -130.0, 51.0, 71.5],
    ['DC', -77.1, -76.9, 38.8, 39.0],
  ];
  for (const [code, minLng, maxLng, minLat, maxLat] of boxes) {
    if (lng >= minLng && lng <= maxLng && lat >= minLat && lat <= maxLat) return code;
  }
  return null;
}

export function resolveMapPointState(point) {
  return (
    coerceStateCode(point?.state) ||
    stateFromStoreLocation(point?.storeLocation || point?.label) ||
    stateFromLatLng(point?.lat, point?.lng) ||
    null
  );
}

export function countUniqueMapStates(points = []) {
  const set = new Set();
  points.forEach((p) => {
    const code = resolveMapPointState(p);
    if (code) set.add(code);
  });
  return set.size;
}
